import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { MissionDescriptionSkin, SectionStatus, ValidityConditionType } from '@flipster/apis/v1/rewardHub/types';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import Num from '@flipster/utils/num';

import { useAuth } from '../../../../../shared/auth/state';
import { rewardHubV2Bunja } from '../../../state';
import { RegularMission } from '../../../types';
import { getDaysUntilExpiration, getLocalizedText, getMissionStatusFlags, getRewardAmount } from '../../utils';

interface UseMultipleMissionsDataParams {
  missions: RegularMission[];
  validityConditions: ValidityConditionType[];
  sectionStatus: SectionStatus;
  sectionDescription?: MissionDescriptionSkin;
}

export const useMultipleMissionsData = ({
  missions,
  validityConditions,
  sectionStatus,
  sectionDescription,
}: UseMultipleMissionsDataParams) => {
  const { user } = useAuth();
  const locale = useSupportedLocale();
  const { missionStatusAtom, feeVoucherRewardAtom } = useBunja(rewardHubV2Bunja);
  const missionStatus = useAtomValue(missionStatusAtom);
  const feeVoucherRewardRecord = useAtomValue(feeVoucherRewardAtom);

  const filteredMissions = missions.filter((mission) => mission.spec);
  const totalMissionCount = filteredMissions.length;
  const completedMissionCount = filteredMissions.filter((mission) => mission.status?.isComplete).length;
  const claimableMissions = filteredMissions.filter(
    (mission) => mission.status && getMissionStatusFlags(mission.status).isClaimable,
  );
  const firstAssignedMission = filteredMissions.find(
    (mission) =>
      getMissionStatusFlags(mission.status).isAssigned &&
      getMissionStatusFlags(mission.status).isValid &&
      !getMissionStatusFlags(mission.status).isComplete,
  );
  const alwaysShowMissions: RegularMission[] = [...claimableMissions, firstAssignedMission].filter(
    (m): m is RegularMission => m != null,
  );
  const milestones = filteredMissions.filter(
    (mission) => getMissionStatusFlags(mission.status).isAssigned && getMissionStatusFlags(mission.status).isValid,
  );
  const claimedMissions = filteredMissions.filter(
    (mission) => getMissionStatusFlags(mission.status).isClaimed || getMissionStatusFlags(mission.status).isSettled,
  );
  const expiredMissions = filteredMissions.filter(
    (mission) => getMissionStatusFlags(mission.status).isExpired && getMissionStatusFlags(mission.status).isAssigned,
  );
  const rejectedMissions = filteredMissions.filter((mission) => getMissionStatusFlags(mission.status).isRejected);

  const totalReward = filteredMissions
    .flatMap(
      (mission) =>
        mission?.spec?.completionRewards.map((reward) => Number(getRewardAmount(reward, feeVoucherRewardRecord))) ?? [],
    )
    .reduce((acc, curr) => acc.plus(Num(curr)), Num(0));

  const sectionValidity = sectionStatus?.validity;
  const daysUntilExpiration = getDaysUntilExpiration(validityConditions, user?.createdAt);
  const isInProgress =
    sectionValidity === 'VALID' &&
    filteredMissions.some((mission) => !getMissionStatusFlags(mission.status).isComplete);
  const showExpiredTime = Boolean(isInProgress && daysUntilExpiration);

  const sectionTitle = getLocalizedText(sectionDescription?.head?.localizationTable, locale);
  const tooltipText = getLocalizedText(sectionDescription?.tail?.localizationTable, locale);

  return {
    missionStatus,
    feeVoucherRewardRecord,
    filteredMissions,
    totalMissionCount,
    completedMissionCount,
    claimableMissions,
    firstAssignedMission,
    alwaysShowMissions,
    milestones,
    claimedMissions,
    expiredMissions,
    rejectedMissions,
    totalReward,
    daysUntilExpiration,
    isInProgress,
    showExpiredTime,
    sectionTitle,
    tooltipText,
  };
};
